/**
 * iter 187.g - DeployBundleTile.js
 *
 * Admin dashboard marker showing which code bundle is currently
 * serving traffic and the timestamp it started. Reads from
 * GET /api/admin/deploy-version which returns:
 *   {
 *     bundle_tag: "iter-187 bundle (c / d / e / f / g)",
 *     code_verified_at: "2026-02-27",
 *     server_started_at: "<ISO8601 UTC>"
 *   }
 *
 * Backend restarts on every deploy, so server_started_at is a
 * reliable proxy for "when did this bundle go live" - accurate to
 * the second. Use this to draw a clean before/after line for
 * completion-rate comparison across combined-iteration deploys.
 */
import { useEffect, useState } from 'react';
import { formatCentralFull } from '../../utils/formatCentral';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// iter 187.l - "Serving since" now renders in Central Time (with an
// explicit "CT" tag) via the platform-wide formatCentralFull SSoT.
// Was formerly UTC (engineering-facing default); switched under the
// timezone consistency rule so the admin tile matches every other
// mundalo.com timestamp surface.
function formatStarted(iso) {
  if (!iso) return { absolute: '-', relative: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { absolute: iso, relative: '' };
  const absolute = formatCentralFull(iso);
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  let relative;
  if (secs < 60) relative = `${secs}s ago`;
  else if (secs < 3600) relative = `${Math.floor(secs / 60)}m ago`;
  else if (secs < 86400) relative = `${Math.floor(secs / 3600)}h ago`;
  else relative = `${Math.floor(secs / 86400)}d ago`;
  return { absolute, relative };
}

export default function DeployBundleTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/admin/deploy-version`, {
          credentials: 'include',
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setData(await resp.json());
      } catch (e) {
        setErr(String(e));
      }
    })();
  }, []);

  const started = formatStarted(data?.server_started_at);

  return (
    <div
      data-testid="deploy-bundle-tile"
      className="rounded-lg border border-slate-300 bg-white p-5 shadow-sm"
    >
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-slate-600">
          Deployed Bundle
        </h3>
        <span className="font-mono text-[10px] text-slate-400">
          /admin/deploy-version
        </span>
      </div>

      {err && (
        <p data-testid="deploy-bundle-error" className="text-sm text-red-600">
          {err}
        </p>
      )}

      {!err && !data && (
        <p className="text-sm text-slate-500">Loading…</p>
      )}

      {data && (
        <div className="space-y-2 text-sm">
          <p
            data-testid="deploy-bundle-tag"
            className="font-semibold text-amber-700"
          >
            {data.bundle_tag}
          </p>
          <p className="text-slate-700">
            <span className="text-slate-500">Bundle finalized:</span>{' '}
            <span data-testid="deploy-bundle-finalized" className="font-medium">
              {data.bundle_finalized_at || '-'}
            </span>
            <span className="ml-2 text-xs text-slate-400">
              (code-audit date, not the visa SSoT)
            </span>
          </p>
          <p className="text-slate-700">
            <span className="text-slate-500">Serving since:</span>{' '}
            <span data-testid="deploy-bundle-started-abs" className="font-medium">
              {started.absolute}
            </span>
            {started.relative && (
              <span
                data-testid="deploy-bundle-started-rel"
                className="ml-2 text-slate-500"
              >
                ({started.relative})
              </span>
            )}
          </p>
          <p className="pt-2 text-xs text-slate-500 leading-relaxed">
            Backend restarts on every deploy - use "Serving since" as
            the before/after cutoff for completion-rate comparisons.
            "Bundle finalized" is the code-audit date and is unrelated
            to the visa SSoT&rsquo;s VERIFIED_AT.
          </p>
        </div>
      )}
    </div>
  );
}
